import { useEffect, useState } from "react";
import { FolderPlus } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { createFolder, type DMSFolder } from "../../lib/dmsApi";

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualSiteId: string;
  parent?: DMSFolder | null;
  onCreated?: (folder: DMSFolder) => void;
}

export function CreateFolderModal({
  isOpen, onClose, virtualSiteId, parent = null, onCreated,
}: CreateFolderModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setError(null);
    }
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Folder name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const folder = await createFolder({
        virtual_site_id: virtualSiteId,
        name: name.trim(),
        parent_folder_id: parent?.id ?? null,
        description: description.trim() || undefined,
      });
      onCreated?.(folder);
      onClose();
    } catch (err) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error ? err.message : String(err);
      setError(msg ?? "Could not create folder.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={parent ? `New folder in "${parent.name}"` : "New folder"}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-surface-300 mb-1.5">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="2026 Inspection Reports"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-surface-300 mb-1.5">
            Description (optional)
          </label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" isLoading={submitting}>
            <FolderPlus className="h-4 w-4" />
            Create folder
          </Button>
        </div>
      </form>
    </Modal>
  );
}
